import { ProductTierSku } from '@vendia/management-api-types'
import { useLocation, useParams } from 'react-router-dom'
import { LinkItem, SideNavLinkItem } from 'src/components/navigation/side-nav-link-item'
import { UniFeatures } from 'src/types/types'
import { useGetOrg } from 'src/utils/hooks/use-get-org'
import { useGetUni } from 'src/utils/hooks/use-get-uni'
import { useIsEnterpriseOrgMember } from 'src/utils/hooks/use-is-org-member'

import Button from '../buttons/button'

declare global {
  interface Window {
    pendo?: {
      isReady?: () => boolean
      showGuideById?: (id: string) => void
    }
  }
}

export function UniSideNav() {
  const { id: uniName, node } = useParams()
  const getUniQuery = useGetUni()
  const uni = getUniQuery?.data?.getUni
  const nodeZero = uni?.nodes?.[0]
  const nodeName = node || nodeZero?.name
  const basePath = `/uni/${uniName}/${nodeName}/`
  const isVendiaTablesEnabled = uni?.features?.includes(UniFeatures.VENDIA_TABLES_ENABLED)
  const { data: isEnterprise } = useIsEnterpriseOrgMember()
  const getOrg = useGetOrg()
  const productTierSku = getOrg?.data?.getOrganization?.productTier?.sku
  // Product tier takes priority over share tier for determining MCP vs Enterprise UI
  const showMcpUI = productTierSku
    ? productTierSku === ProductTierSku.Individual || productTierSku === ProductTierSku.Pro
    : !isEnterprise

  const links: LinkItem[] = [
    {
      path: `getting-started`,
      label: 'Getting Started',
      iconName: 'lucide-zap',
      isActive: false,
    },
    {
      path: `mcp-dashboard`,
      label: 'Dashboard',
      iconName: 'lucide-house-plug',
      isActive: false,
      dataTour: 'nav-dashboard',
    },
    {
      path: `chat`,
      label: 'Chat',
      iconName: 'lucide-message-square',
      isActive: false,
      dataTour: 'nav-chat',
    },
    {
      path: `mcp-gateway`,
      label: 'MCP Servers',
      iconName: 'lucide-cable',
      isActive: false,
      dataTour: 'nav-mcp-servers',
    },
    {
      path: `storage-connections`,
      label: 'Storage Connections',
      iconName: 'lucide-cloudy',
      isActive: false,
      dataTour: 'nav-storage-connections',
    },
    {
      path: `api-connections`,
      label: 'API Connections',
      iconName: 'lucide-book-open',
      isActive: false,
      dataTour: 'nav-api-connections',
    },
    {
      path: `govern`,
      label: 'Govern',
      iconName: 'lucide-scale',
      isActive: false,
      dataTour: 'nav-govern',
    },
    {
      path: `analyze`,
      label: 'Observe',
      iconName: 'lucide-logs',
      isActive: false,
      additionalMatchingPaths: ['transactions'],
      dataTour: 'nav-observe',
    },
  ]

  links.push({
    path: `/org/overview`,
    label: 'Admin',
    iconName: 'lucide-shield',
    isActive: false,
    dataTour: 'nav-admin',
    state: { from: basePath },
  })

  const enterpriseLinks: LinkItem[] = [
    {
      path: `getting-started`,
      label: 'Getting Started',
      iconName: 'lucide-zap',
      isActive: false,
    },
    {
      path: `mcp-dashboard`,
      label: 'Dashboard',
      iconName: 'lucide-house-plug',
      isActive: false,
    },
    {
      path: `chat`,
      label: 'Chat',
      iconName: 'lucide-message-square',
      isActive: false,
    },
    {
      isSectionHeader: true,
      label: 'Connected Data',
    },
    {
      path: `mcp-gateway`,
      label: 'MCP Servers',
      iconName: 'lucide-cable',
      isActive: false,
    },
    {
      path: `storage-connections`,
      label: 'Storage Connections',
      iconName: 'lucide-cloudy',
      isActive: false,
    },
    {
      path: `api-connections`,
      label: 'API Connections',
      iconName: 'lucide-book-open',
      isActive: false,
    },
    {
      isSectionHeader: true,
      label: 'Platform Data',
    },
    {
      path: `entities`,
      label: 'Operational',
      iconName: 'lucide-database',
      isActive: false,
    },
    {
      path: `files`,
      label: 'Files',
      iconName: 'lucide-folder',
      isActive: false,
    },
    ...(isVendiaTablesEnabled
      ? [
          {
            path: `data`,
            label: 'Tables',
            iconName: 'lucide-table-2',
            isActive: false,
          },
          {
            path: `products`,
            label: 'Data Products',
            iconName: 'lucide-box',
            isActive: false,
          },
        ]
      : []),
    {
      isDivider: true,
    },
    {
      path: 'workflows',
      label: 'Workflows',
      iconName: 'lucide-arrow-left-right',
      isActive: false,
    },
    {
      path: `govern`,
      label: 'Govern',
      iconName: 'lucide-scale',
      isActive: false,
    },
    {
      path: `analyze`,
      label: 'Observe',
      iconName: 'lucide-logs',
      isActive: false,
      additionalMatchingPaths: ['transactions'],
    },
    {
      path: `settings`,
      label: 'Settings',
      iconName: 'lucide-settings',
      isActive: false,
    },
  ]

  const currentLinks = showMcpUI ? links : enterpriseLinks

  const location = useLocation()
  const linkListItems = currentLinks?.map((link, index) => {
    // If path starts with '/', it's absolute and shouldn't have basePath prepended
    const path = link.path?.startsWith('/') ? link.path : `${basePath}${link.path}`
    // Normalize trailing slash and compare to find active path
    const currentPath = location.pathname.replace(/\/+$/, '')
    const currentLinkPath = path.replace(/\/+$/, '')
    // "loose match" for entity explorer (see old sidenav)
    const isActive =
      currentPath.includes(currentLinkPath) ||
      (link.additionalMatchingPaths?.some((p) => currentPath.includes(`/${p}`)) ?? false)
    link.isActive = isActive
    link.path = path

    return <SideNavLinkItem key={`${path}-${index}`} linkItem={link} />
  })

  if (!nodeName || !uni) {
    return <div className='px-2' />
  }

  const isPendoReady = window.pendo?.isReady?.() === true

  const handleFeedbackClick = () => {
    window.pendo?.showGuideById?.('o10LT_gtz-FV-QW0eJktNA6rMGY')
  }

  return (
    <div
      className={
        'max-h-[calc(100vh-76px)] w-56 overflow-y-auto border-t border-slate-200 bg-white p-4 ring-1 ring-slate-200'
      }
    >
      <div className='flex h-full flex-col'>
        <div className='flex-1'>{linkListItems}</div>
        {isPendoReady && (
          <Button
            kind='secondary'
            className='mt-4 border-t border-slate-200 pt-4'
            icon='lucide-message-square'
            onClick={handleFeedbackClick}
          >
            Share Feedback
          </Button>
        )}
      </div>
    </div>
  )
}
